import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { projectRouter } from "@v2/trpc/routes/project";
import { RotateCcw } from "lucide-react";
import { injectStyleLibraryCssIntoHtml } from "replo-utils/lib/html";

import { SitePreviewCard } from "../SitePreviewCard";
import { CustomStylesErrorCard } from "./CustomStylesErrorCard";
import { CustomStylesInputCard } from "./CustomStylesInputCard";
import { CustomStylesLoadingCard } from "./CustomStylesLoadingCard";

interface CustomStylesCardProps {
  pages: string[];
  pageHtmlByRoute: Record<string, string>;
  isSelected: boolean;
  onSelect: () => void;
  brandDomain: string | null;
  hasBrandData: boolean;
  logoUrl: string | null;
  extractedStyleLibrary: StyleLibrary | null;
  isPending: boolean;
  hasError: boolean;
  pendingUrl: string;
  onPullStyles: (
    url: string,
    useBrandData: boolean,
    brandColors?: { hex: string; type: string; brightness: number }[],
    brandFonts?: { name: string; type: string }[],
  ) => Promise<void>;
  onCancel: () => void;
  onRetry: () => void;
}

export function CustomStylesCard({
  pages,
  pageHtmlByRoute,
  isSelected,
  onSelect,
  brandDomain,
  hasBrandData,
  logoUrl,
  extractedStyleLibrary,
  isPending,
  hasError,
  pendingUrl,
  onPullStyles,
  onCancel,
  onRetry,
}: CustomStylesCardProps) {
  const { data: brandData } = projectRouter.getBrandData.useQuery(
    { domain: brandDomain ?? "" },
    { enabled: hasBrandData && Boolean(brandDomain) },
  );

  const handleBrandPullStyles = () => {
    if (brandDomain) {
      void onPullStyles(brandDomain, true, brandData?.colors, brandData?.fonts);
    }
  };

  const handleUrlPullStyles = (url: string) => {
    void onPullStyles(url, false);
  };

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRetry();
  };

  const brandColors = React.useMemo(() => {
    if (!brandData?.colors || brandData.colors.length === 0) {
      return ["#ffffff", "#eeebf6", "#a3a3b7", "#1e1d1d", "#4d25d9", "#fed4d4"];
    }
    const colors = brandData.colors.slice(0, 6).map((c) => c.hex);
    while (colors.length < 6) {
      colors.push("#f5f5f5");
    }
    return colors;
  }, [brandData]);

  const brandFonts = React.useMemo(() => {
    if (!brandData?.fonts || brandData.fonts.length === 0) {
      return { heading: "Space Grotesk", body: "Helvetica" };
    }
    const heading =
      brandData.fonts.find((f) => f.type === "title")?.name ??
      brandData.fonts[0]?.name ??
      "Space Grotesk";
    const body =
      brandData.fonts.find((f) => f.type === "body")?.name ??
      brandData.fonts[1]?.name ??
      "Helvetica";
    return { heading, body };
  }, [brandData]);

  const handleCancel = () => {
    onCancel();
  };

  const customStyleName = React.useMemo(() => {
    if (brandData?.name) {
      return brandData.name;
    }

    const urlSource = pendingUrl || brandDomain || "";
    if (!urlSource) {
      return "Custom";
    }

    try {
      const parsedUrl = new URL(
        urlSource.startsWith("http") ? urlSource : `https://${urlSource}`,
      );
      const hostname = parsedUrl.hostname.replace(/^www\./, "");
      const baseName = hostname.split(".")[0] ?? "";
      if (!baseName) {
        return "Custom";
      }
      return `${baseName[0]?.toUpperCase() ?? ""}${baseName.slice(1)}`;
    } catch {
      return "Custom";
    }
  }, [brandData?.name, brandDomain, pendingUrl]);

  if (isPending) {
    return (
      <CustomStylesLoadingCard
        isSelected={isSelected}
        onSelect={onSelect}
        onCancel={handleCancel}
        pendingUrl={pendingUrl}
      />
    );
  }

  if (hasError) {
    return (
      <CustomStylesErrorCard
        isSelected={isSelected}
        onSelect={onSelect}
        onRetry={handleRetry}
      />
    );
  }

  if (extractedStyleLibrary) {
    const h1Typography = extractedStyleLibrary.typography?.h1;
    const subtleBackgroundColor =
      extractedStyleLibrary.colors?.neutrals.subtle ?? null;

    const injectedPageHtmlByRoute: Record<string, string> = {};
    for (const [route, html] of Object.entries(pageHtmlByRoute)) {
      injectedPageHtmlByRoute[route] = injectStyleLibraryCssIntoHtml(
        html,
        extractedStyleLibrary,
      );
    }

    return (
      <div className="relative overflow-visible">
        <SitePreviewCard
          styleId="custom"
          styleName={customStyleName}
          fontUrl={h1Typography?.url ?? null}
          fontFamily={h1Typography?.name ?? "Inter"}
          subtleBackgroundColor={subtleBackgroundColor}
          pages={pages}
          pageHtmlByRoute={injectedPageHtmlByRoute}
          isSelected={isSelected}
          onSelect={onSelect}
        />
        <div className="absolute -top-3 -right-2 z-20">
          <Button
            variant="secondary"
            size="base"
            onClick={handleRetry}
            aria-label="Retry style extraction"
            UNSAFE_className="px-3 py-2"
          >
            <span className="flex items-center gap-1.5">
              <span>Retry</span>
              <RotateCcw className="size-4" strokeWidth={2} />
            </span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CustomStylesInputCard
      isSelected={isSelected}
      onSelect={onSelect}
      hasBrandData={hasBrandData}
      onBrandPullStyles={handleBrandPullStyles}
      onUrlPullStyles={handleUrlPullStyles}
      brandColors={brandColors}
      brandFonts={brandFonts}
      logoUrl={logoUrl}
      brandName={brandData?.name}
    />
  );
}
